import Search from '@mui/icons-material/Search'
import { CircularProgress, InputAdornment, TextField, Box, Button } from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useState, useMemo } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { SentryRefHandler } from 'src/consumer/components/helperComponents'
import { prepareBookedEventDataForListing } from 'src/mentor/components/MentorEventsPage/components/utils'
import { MentorUpcomingPastEventsTable } from 'src/mentor/components/MentorEventsPage/MentorEventsComponentsAndHelpers'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMentorCommunicationTargetsBatch } from 'src/mentor/services/practitionerContactService'
import BookedEventLogsDrawer from 'src/clinic/components/ClinicManageBookingsPage/components/BookedEventLogsDrawer'
import BookedEventsFilters from './BookedEventsFilters'
import { SESSION_PLATFORM_ONLINE, SESSION_PLATFORM_OFFLINE, BOOKING_STATUSES, BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'
import styled from 'styled-components'
import { getPaymentLinksForBookedEvents } from 'src/clinic/components/ClinicManageBookingsPage/utils'

const SearchContainer = styled(Box)`
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`

/**
 * Core BookingsListing Component
 * This is a reusable component that can be customized for different portals (mentor, support)
 * 
 * @param {object} props
 * @param {function} props.fetchBookedSessions - Function to fetch booked sessions (page, params) => Promise<Response>
 * @param {string} props.listingApiSignature - API endpoint signature (for reference)
 * @param {function} props.listOfPaymentLinksApiSignature - Optional function to fetch payment links
 * @param {boolean} props.showMentorName - Whether to show mentor name in the table
 * @param {boolean} props.hideActions - Whether to hide action buttons (start/join/checkin)
 * @param {function} props.sortingHandler - Sorting handler function
 * @param {string} props.eventType - Event type filter
 * @param {boolean} props.showOnlyPaidEvents - Show only paid events
 * @param {string} props.logsApiEndpoint - API endpoint for logs
 * @param {string} props.meetingSrcFilter - Filter by meeting source ('aumhum' for video, 'offline' for in-person)
 * @param {boolean} props.showOnlyUnconfirmed - Show only unconfirmed bookings
 * @param {boolean} props.filterByBookedStatus - Filter by booked status (true = only booked, false = exclude booked)
 * @param {boolean} props.hideSessionTypeFilter - Hide Session Type filter
 * @param {object} props.mentorProfileData - Mentor profile data
 * @param {function} props.onClickJoin - Handler for join/start button click (optional, only if hideActions is false)
 * @param {function} props.onOpenDetails - Handler for opening details drawer (optional)
 * @param {function} props.onRescheduleBooking - Optional reschedule override handler
 * @param {string} props.cancelAppointmentApiSignature - Optional cancel override API signature
 * @param {boolean} props.skipCancelCheckInGuard - When true, opens cancel without mentor check-in gating
 * @param {function} props.rightSideCustomComponent - Optional custom booking-strip actions renderer
 * @param {boolean} props.hidePatientContactDetails - Hide patient mobile/email from display-only booking UI
 * @param {ref} componentRef - Ref to expose resetData method
 */
const CoreBookingsListing = forwardRef(
  (
    {
      fetchBookedSessions,
      listingApiSignature = '/api/secure/teacher/booked-slots/all/paginated',
      listOfPaymentLinksApiSignature,
      showMentorName = false,
      hideActions = false,
      hidePortalVoiceCallAction = false,
      sortingHandler,
      eventType,
      showOnlyPaidEvents = false,
      logsApiEndpoint = '/api/secure/teacher/booked-event-info/logs/get',
      meetingSrcFilter, // 'aumhum' for video call, 'offline' for in-person
      showOnlyUnconfirmed = false, // Show only unconfirmed bookings
      filterByBookedStatus = true, // Filter by booked status (true = only booked, false = exclude booked)
      hideSessionTypeFilter = false, // Hide Session Type filter
      eventTypeOptions = null,
      mentorProfileData = null,
      onClickJoin = null,
      onOpenDetails = null,
      onRescheduleBooking = null,
      cancelAppointmentApiSignature = null,
      skipCancelCheckInGuard = false,
      rightSideCustomComponent = null,
      hidePatientContactDetails = false,
    },
    componentRef,
  ) => {
    const [hasNextPage, setHasNextPage] = useState(true)
    const [pageNumber, setPageNumber] = useState(0)
    const [mentorBookedSlotsData, setMentorBookedSlotsData] = useState([])
    const [mentorBookedSlotsLoading, setMentorBookedSlotsLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
    const [isSearching, setIsSearching] = useState(false)
    const [showBookedEventLogsDialog, setShowBookedEventLogsDialog] = useState(false)
    const [selectedBookedEvent, setSelectedBookedEvent] = useState(null)
    const [filters, setFilters] = useState({})
    const [communicationTargetsByBookedEventId, setCommunicationTargetsByBookedEventId] = useState({})
    const [communicationTargetsLoading, setCommunicationTargetsLoading] = useState(false)

    const [sentryRef] = useInfiniteScroll({
      hasNextPage,
      onLoadMore: getNextBookedSessionsPage,
      rootMargin: '0px 0px 20px 0px',
    })

    // Debounce search query
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearchQuery(searchQuery)
      }, 500) // 500ms debounce delay

      return () => clearTimeout(timer)
    }, [searchQuery])

    // Build API params from filters
    const buildApiParams = useMemo(() => {
      const params = {
        page: 0,
        size: 20,
        sort: sortingHandler || 'startTime,DESC',
      }

      // Date range filters
      if (filters.startTime) {
        params.startTime = filters.startTime
      }
      if (filters.endTime) {
        params.endTime = filters.endTime
      }

      // Event types filter
      if (filters.eventTypes && filters.eventTypes.length > 0) {
        params.eventTypes = filters.eventTypes
      } else if (eventType) {
        params.eventTypes = Array.isArray(eventType) ? eventType : [eventType]
      }

      // Status filter
      if (filters.statusIds && filters.statusIds.length > 0) {
        params.statusIds = filters.statusIds
      }

      // Payment status filter
      if (filters.paymentStatusIds && filters.paymentStatusIds.length > 0) {
        params.paymentStatusIds = filters.paymentStatusIds
      }

      // Blocked sessions flag
      if (filters.showBlockedSession !== undefined) {
        params.showBlockedSession = filters.showBlockedSession
      } else {
        params.showBlockedSession = !showOnlyPaidEvents
      }

      // Search query
      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery
      }

      return params
    }, [filters, eventType, showOnlyPaidEvents, sortingHandler, debouncedSearchQuery])

    async function cleanExistingData() {
      setMentorBookedSlotsData([])
      setPageNumber(0)
      setHasNextPage(true)
      await setBookedSessionsFirstPage()
    }

    async function setBookedSessionsFirstPage() {
      setIsSearching(true)
      const params = { ...buildApiParams, page: 0 }
      const resp = await fetchBookedSessions(0, listingApiSignature, params)
      if (resp?.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          const respData = getDataMapFromCommonResponse(resp)
          let bookedEventData = prepareBookedEventDataForListing(respData)

          if (listOfPaymentLinksApiSignature) {
            const paymentLinks = await getPaymentLinksForBookedEvents(
              bookedEventData?.map((item) => item?.id),
              listOfPaymentLinksApiSignature,
            )
            bookedEventData = bookedEventData?.map((item) => {
              const paymentLink = paymentLinks?.filter(
                (paymentLink) => paymentLink?.bookedEventId === item?.id,
              )
              return {
                ...item,
                paymentLinks: paymentLink,
              }
            })
          }

          if (bookedEventData?.length <= 0) {
            setHasNextPage(false)
            setMentorBookedSlotsLoading(false)
            setIsSearching(false)
            return
          }
          console.log('bookedEventData', bookedEventData)
          setMentorBookedSlotsData(bookedEventData)
        }
      }
      setIsSearching(false)
    }

    async function getNextBookedSessionsPage() {
      setMentorBookedSlotsLoading(true)
      const nextPageNumber = pageNumber + 1
      const params = { ...buildApiParams, page: nextPageNumber }
      const resp = await fetchBookedSessions(nextPageNumber, listingApiSignature, params)
      if (resp?.status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          const respData = getDataMapFromCommonResponse(resp)
          const bookedEventData = prepareBookedEventDataForListing(respData)
          if (bookedEventData?.length <= 0) {
            setHasNextPage(false)
            setMentorBookedSlotsLoading(false)
            return
          }
          setPageNumber(nextPageNumber)
          setMentorBookedSlotsData((prev) => [...prev, ...bookedEventData])
        }
      } else {
        setHasNextPage(false)
      }
      setMentorBookedSlotsLoading(false)
    }

    async function setup() {
      // Only show main loading on initial load, not on search
      if (!debouncedSearchQuery) {
        setMentorBookedSlotsLoading(true)
      }
      await cleanExistingData()
      if (!debouncedSearchQuery) {
        setMentorBookedSlotsLoading(false)
      }
    }

    useImperativeHandle(componentRef, () => ({
      resetData: cleanExistingData,
    }))

    function openBookedEventLogsDialog(bookedEvent) {
      setSelectedBookedEvent(bookedEvent)
      setShowBookedEventLogsDialog(true)
    }

    function closeBookedEventLogsDialog() {
      setShowBookedEventLogsDialog(false)
      setSelectedBookedEvent(null)
    }

    useEffect(() => {
      setup()
    }, [sortingHandler, debouncedSearchQuery, filters])

    const handleFiltersChange = (newFilters) => {
      setFilters(newFilters)
    }

    const handleFiltersReset = () => {
      setFilters({})
    }

    const filteredBookedEvents = useMemo(
      () =>
        mentorBookedSlotsData.filter((event) => {
          if (filterByBookedStatus !== undefined) {
            const isBooked = event?.status === BOOKING_STATUSES.BOOKED
            if (filterByBookedStatus && !isBooked) {
              return false
            }
            if (filterByBookedStatus === false && isBooked) {
              return false
            }
          }

          if (meetingSrcFilter && event?.meetingSrc !== meetingSrcFilter) {
            return false
          }

          if (showOnlyUnconfirmed) {
            const isUnconfirmed =
              event?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
              event?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
            if (!isUnconfirmed) {
              return false
            }
          }

          return true
        }),
      [
        filterByBookedStatus,
        meetingSrcFilter,
        mentorBookedSlotsData,
        showOnlyUnconfirmed,
      ],
    )

    const filteredBookedEventIdsKey = useMemo(
      () => filteredBookedEvents.map((event) => event?.id).filter(Boolean).join(','),
      [filteredBookedEvents],
    )

    useEffect(() => {
      let isCancelled = false

      async function loadCommunicationTargets() {
        if (!filteredBookedEventIdsKey) {
          setCommunicationTargetsLoading(false)
          return
        }

        setCommunicationTargetsLoading(true)
        const result = await getMentorCommunicationTargetsBatch(
          filteredBookedEvents.map((event) => event?.id).filter(Boolean),
        )

        if (isCancelled) {
          return
        }

        if (result?.ok && result?.targetsByBookedEventId) {
          setCommunicationTargetsByBookedEventId((currentValue) => ({
            ...currentValue,
            ...result.targetsByBookedEventId,
          }))
        }
        setCommunicationTargetsLoading(false)
      }

      void loadCommunicationTargets()

      return () => {
        isCancelled = true
      }
    }, [filteredBookedEventIdsKey, filteredBookedEvents])

    return (
      <>
        <BookedEventsFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleFiltersReset}
          hideSessionTypeFilter={hideSessionTypeFilter}
          eventTypeOptions={eventTypeOptions || undefined}
        />
        <SearchContainer>
          <TextField
            fullWidth
            placeholder={
              hidePatientContactDetails
                ? 'Search by patient name, patient number, event type, or clinic...'
                : 'Search by patient name, number, mobile, email, alternate contacts, event type, or clinic...'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching ? <CircularProgress size={20} /> : <Search />}
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </SearchContainer>
        <MentorUpcomingPastEventsTable
          allEvents={filteredBookedEvents}
          loadingSlots={mentorBookedSlotsLoading}
          mentorProfileData={mentorProfileData}
          onClickJoin={onClickJoin || (() => {})}
          refreshEvents={() => {
            cleanExistingData()
          }}
          showMentorName={showMentorName}
          hideActions={hideActions}
          hidePortalVoiceCallAction={hidePortalVoiceCallAction}
          openBookedEventLogsDialog={openBookedEventLogsDialog}
          onRescheduleBooking={onRescheduleBooking}
          cancelAppointmentApiSignature={cancelAppointmentApiSignature}
          skipCancelCheckInGuard={skipCancelCheckInGuard}
          communicationTargetsByBookedEventId={communicationTargetsByBookedEventId}
          communicationTargetsLoading={communicationTargetsLoading}
          hidePatientContactDetails={hidePatientContactDetails}
          rightSideCustomComponent={
            onOpenDetails || rightSideCustomComponent
              ? ({ event, defaultActions }) => {
                  const customActions = rightSideCustomComponent
                    ? rightSideCustomComponent({ event, defaultActions })
                    : null

                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 1,
                        maxWidth: 320,
                      }}
                    >
                      {customActions}
                      {onOpenDetails ? (
                        <Button
                          onClick={() => onOpenDetails(event)}
                          variant="outlined"
                          size="small"
                          sx={{
                            padding: '6px 16px',
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            color: 'rgba(16, 185, 129, 0.95)',
                            borderColor: 'rgba(16, 185, 129, 0.3)',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 500,
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                            textTransform: 'none',
                            '&:hover': {
                              backgroundColor: 'rgba(16, 185, 129, 0.15)',
                              borderColor: 'rgba(16, 185, 129, 0.5)',
                            },
                          }}
                        >
                          Details
                        </Button>
                      ) : null}
                    </Box>
                  )
                }
              : undefined
          }
        />
        {mentorBookedSlotsLoading ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '10px 0px',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <SentryRefHandler
            hasNextPage={hasNextPage}
            sentryRef={sentryRef}
            loading={mentorBookedSlotsLoading}
          />
        )}
        {selectedBookedEvent && (
          <BookedEventLogsDrawer
            open={showBookedEventLogsDialog}
            onClose={closeBookedEventLogsDialog}
            bookedSlot={{
              ...selectedBookedEvent,
              id: selectedBookedEvent?.id,
              startTime: selectedBookedEvent?.startTime,
              endTime: selectedBookedEvent?.endTime,
              patientInfo: {
                name: selectedBookedEvent?.patientInfoData?.name || selectedBookedEvent?.userName,
                patientNumber: selectedBookedEvent?.patientInfoData?.patientNumber,
                gender: selectedBookedEvent?.patientInfoData?.gender,
                dateOfBirth: selectedBookedEvent?.patientInfoData?.dateOfBirth,
                mobile:
                  hidePatientContactDetails ||
                  selectedBookedEvent?.patientContactDetailsVisible === false
                    ? undefined
                    : selectedBookedEvent?.patientInfoData?.mobile,
              },
              mentorName: selectedBookedEvent?.mentorName,
              eventType: selectedBookedEvent?.eventType,
            }}
            apiEndpoint={logsApiEndpoint}
          />
        )}
      </>
    )
  },
)

CoreBookingsListing.displayName = 'CoreBookingsListing'

export default CoreBookingsListing
